import { useState } from 'react';
import {
  Plus,
  CreditCard,
  Pencil,
  Trash2,
  Check,
  Calendar,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  ArrowDownRight,
} from 'lucide-react';
import { useInstallments } from '@hooks/useInstallments';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import PageHeader from '@components/layout/PageHeader';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import PageTutorial from '@components/common/PageTutorial';
import InstallmentForm from '@components/installments/InstallmentForm';
import { formatCurrency, formatDate } from '@utils/formatters';
import { clsx } from 'clsx';

// ─── MetricCard ───────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, subtitle, icon, iconBg, valueColor, children }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
      <div className={clsx('p-2.5 rounded-xl', iconBg)}>{icon}</div>
    </div>
    <p className={clsx('text-2xl font-bold', valueColor)}>{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    {children}
  </div>
);

const Installments = () => {
  const {
    installments,
    totalAmount,
    remainingAmount,
    paidAmount,
    isLoading,
    createInstallment,
    deleteInstallment,
    payInstallment,
    isCreating,
    isPaying,
  } = useInstallments();

  const { accounts } = useAccounts();
  const { categories } = useCategories();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [payingInstallment, setPayingInstallment] = useState(null);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedInstallment(null);
    setIsModalOpen(false);
  };

  const handleSubmit = async (data) => {
    await createInstallment(data);
    handleCloseModal();
  };

  const handleDelete = async () => {
    if (selectedInstallment) {
      await deleteInstallment(selectedInstallment.installmentId);
      setIsDeleteModalOpen(false);
      setSelectedInstallment(null);
    }
  };

  const handlePayInstallment = async (installment, scheduleItem) => {
    setPayingInstallment(`${installment.installmentId}-${scheduleItem.number}`);
    try {
      await payInstallment({
        installmentId: installment.installmentId,
        installmentNumber: scheduleItem.number,
        paymentDate: new Date().toISOString().split('T')[0],
      });
    } finally {
      setPayingInstallment(null);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.categoryId === categoryId);
    return category ? `${category.icon} ${category.name}` : categoryId;
  };

  if (isLoading) {
    return <Loading text="Carregando parcelamentos..." />;
  }

  const progress = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
  const progressDisplay = progress.toFixed(0);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Parcelamentos"
        subtitle="Controle suas compras parceladas"
        action={
          <Button leftIcon={<Plus size={18} />} onClick={handleOpenModal}>
            Novo Parcelamento
          </Button>
        }
      />

      <PageTutorial
        pageId="installments"
        title="Compras parceladas"
        steps={[
          'Registre compras parceladas informando valor total ou valor da parcela',
          'Acompanhe o progresso de cada parcelamento e parcelas pagas vs pendentes',
          'Parcelas pendentes aparecem automaticamente na timeline de transacoes',
        ]}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Parcelado"
          value={formatCurrency(totalAmount)}
          subtitle={`${installments.length} parcelamento${installments.length !== 1 ? 's' : ''}`}
          icon={<CreditCard size={18} className="text-primary-600" />}
          iconBg="bg-primary-100 dark:bg-primary-900/30"
          valueColor="text-gray-900 dark:text-white"
        />
        <MetricCard
          title="Já Pago"
          value={formatCurrency(paidAmount)}
          icon={<CheckCircle2 size={18} className="text-emerald-600" />}
          iconBg="bg-emerald-100 dark:bg-emerald-900/30"
          valueColor="text-emerald-600"
        />
        <MetricCard
          title="Restante"
          value={formatCurrency(remainingAmount)}
          icon={<ArrowDownRight size={18} className="text-red-600" />}
          iconBg="bg-red-100 dark:bg-red-900/30"
          valueColor="text-red-600"
        />
        <MetricCard
          title="Progresso Geral"
          value={`${progressDisplay}%`}
          icon={<Clock size={18} className="text-primary-600" />}
          iconBg="bg-primary-100 dark:bg-primary-900/30"
          valueColor="text-primary-600"
        >
          <div className="mt-3 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
        </MetricCard>
      </div>

      {installments.length === 0 ? (
        <EmptyState
          icon={<CreditCard size={32} />}
          title="Nenhum parcelamento cadastrado"
          description="Registre suas compras parceladas para acompanhar os pagamentos."
          action={handleOpenModal}
          actionLabel="Adicionar Parcelamento"
        />
      ) : (
        <div className="space-y-4">
          {installments.map((installment) => {
            const isExpanded = expandedId === installment.installmentId;
            const progressPercent = (installment.paidInstallments / installment.totalInstallments) * 100;

            return (
              <Card key={installment.installmentId} padding="none" className="overflow-hidden">
                {/* Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : installment.installmentId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                        <CreditCard size={24} className="text-red-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {installment.description}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="primary" size="sm">
                            {installment.paidInstallments}/{installment.totalInstallments} parcelas
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {getCategoryName(installment.categoryId)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Parcela</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(installment.installmentAmount)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Restante</p>
                        <p className="text-lg font-bold text-red-600">
                          {formatCurrency(installment.remainingAmount)}
                        </p>
                      </div>
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all duration-700"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{formatCurrency(installment.totalAmount - installment.remainingAmount)} pago</span>
                      <span>{formatCurrency(installment.totalAmount)} total</span>
                    </div>
                  </div>
                </div>

                {/* Expanded Schedule */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Cronograma de Parcelas
                        </h4>
                        <div className="relative group">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInstallment(installment);
                              setIsDeleteModalOpen(true);
                            }}
                            disabled={installment.paidInstallments > 0}
                          >
                            <Trash2 size={14} className="mr-1" />
                            Excluir
                          </Button>
                          {installment.paidInstallments > 0 && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10 pointer-events-none">
                              {installment.paidInstallments} parcela(s) paga(s) — não é possível excluir
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid gap-2">
                        {installment.schedule?.map((item) => (
                          <div
                            key={item.number}
                            className={clsx(
                              'flex items-center justify-between p-3 rounded-lg',
                              item.isPaid
                                ? 'bg-emerald-50 dark:bg-emerald-900/20'
                                : 'bg-white dark:bg-gray-800'
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div className={clsx(
                                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                                item.isPaid
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                              )}>
                                {item.isPaid ? <Check size={16} /> : item.number}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  Parcela {item.number}/{installment.totalInstallments}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Vencimento: {formatDate(item.dueDate)}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {formatCurrency(item.amount)}
                              </p>
                              {item.isPaid ? (
                                <Badge variant="success" size="sm">Pago</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePayInstallment(installment, item);
                                  }}
                                  isLoading={payingInstallment === `${installment.installmentId}-${item.number}`}
                                >
                                  Pagar
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Novo Parcelamento"
        size="md"
      >
        <InstallmentForm
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isLoading={isCreating}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Excluir Parcelamento"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Excluir
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Tem certeza que deseja excluir o parcelamento <strong>{selectedInstallment?.description}</strong>?
          {selectedInstallment?.paidInstallments > 0 && (
            <span className="block mt-2 text-red-600">
              Este parcelamento possui parcelas pagas e não pode ser excluído.
            </span>
          )}
        </p>
      </Modal>
    </div>
  );
};

export default Installments;
